import cn from "classnames";
import { type FC, useMemo } from "react";
import { isFunction } from "lodash";
import EllipsisPopupButton from "components/buttons/ellipsisPopupButton/EllipsisPopupButton";
import { ArchiveLinkButton, EditLinkButton, RemoveLinkButton, RestoreLinkButton } from "components/buttons/linkButtons";
import { Tooltip } from "components/common/tooltip";
import { PricingTypes } from "enums/licensing/pricingTypes";
import { useNavigate } from "react-router";
import { RouteNames } from "enums";
import { useToggle } from "hooks/useToggle";
import { AccountTypeEnum } from "interfaces/Account";
import type { PaymentPack } from "../../../Licensing/Packs/PaymentModal/types";
import SinglePaymentModalContainer from "../../../Licensing/Packs/PaymentModal/container/SinglePaymentModalContainer";

export interface Props {
  id: number;
  title: string;
  thumbnailImageUrl: string;
  isExpired: boolean;
  isTrial: boolean;
  packPricingTypeId?: number;
  isArchived: boolean;
  hasAccountManageRole: boolean;
  onEditLicensing?: (id: number, packPricingTypeId?: number) => void;
  onConvertTrialLicense?: (id: number, packPricingTypeId?: number) => void;
  onExtendLicenses?: (id: number, title: string, thumbnailImageUrl: string, packPricingTypeId?: number) => void;
  onRemovePackFromAccount?: (id: number) => void;
  isAllowedToRemovePackFromAccount?: () => boolean;
  onArchivePack?: (id: number) => void;
  onRestorePack?: (id: number, title: string, thumbnailImageUrl: string, packPricingTypeId?: number) => void;
  hasPurchasePower: boolean;
  isAccountHome: boolean;
  isOwnPack: boolean;
  hasSource: boolean;
  licenseCount: number;
  hasPurchasePackAddon: boolean;
  expirationDate?: Date;
  hasGlobalAccess: boolean;
  pricePerUserPerMonth?: number;
  billingTerm?: string;
  isPurchased?: boolean;
  isMobo: boolean;
  originAccountTypeId: AccountTypeEnum | null;
}

export const AccountPackMenu: FC<Props> = (props: Props) => {
  const {
    id,
    title,
    thumbnailImageUrl,
    isExpired,
    isArchived,
    onEditLicensing,
    onExtendLicenses,
    onRemovePackFromAccount,
    onConvertTrialLicense,
    isAllowedToRemovePackFromAccount,
    onArchivePack,
    hasAccountManageRole,
    onRestorePack,
    hasPurchasePower,
    isAccountHome,
    isOwnPack,
    hasSource,
    packPricingTypeId,
    licenseCount,
    expirationDate,
    hasPurchasePackAddon,
    hasGlobalAccess,
    isTrial,
    pricePerUserPerMonth = 0,
    billingTerm = "Annual",
    isPurchased = true,
    isMobo = false,
    originAccountTypeId,
  } = props;

  const rawPricePerUser = pricePerUserPerMonth * (billingTerm === "Annual" ? 12 : 1);
  const isFree = packPricingTypeId === PricingTypes.Free;
  const isFreeOrOther = packPricingTypeId === PricingTypes.Free || packPricingTypeId === PricingTypes.Other;
  const isPaid = packPricingTypeId === PricingTypes.Paid;
  const bsiUnderMobo = isMobo && originAccountTypeId === AccountTypeEnum.BSI;
  const shouldShowPurchaseButton =
    isPaid && hasPurchasePower && isTrial && isPurchased && !bsiUnderMobo && !hasGlobalAccess;
  const convertTrialLicenseButton =
    !isExpired && isTrial && !isOwnPack && hasGlobalAccess && (!isAccountHome || hasPurchasePower);
  const [isPaymentModalOpen, togglePaymentModal] = useToggle(false);
  const navigate = useNavigate();

  const handlePaymentSuccess = (packId: number) => {
    togglePaymentModal();
    navigate(`/${RouteNames.purchasedPacks}/${packId}/licensing`);
  };

  const getEditLicensingButton = (isDisabled: boolean) => {
    if (isDisabled) {
      return (
        <Tooltip
          content={"You don't have permission to perform this action. Contact you administrator for more information."}
          target={
            <EditLinkButton
              key={cn("EditLicensingButton-", id)}
              onClick={() => {
                onEditLicensing && onEditLicensing(id, packPricingTypeId);
              }}
              text="Edit Licensing"
              isDisabled
            />
          }
          showAlways
        />
      );
    }

    return (
      <EditLinkButton
        key={cn("EditLicensingButton-", id)}
        onClick={() => {
          onEditLicensing && onEditLicensing(id, packPricingTypeId);
        }}
        text="Edit Licensing"
      />
    );
  };

  const extendLicensesButton = (
    <EditLinkButton
      key={cn("ExtendLicensesButton-", id)}
      onClick={() => {
        onExtendLicenses?.(id, title, thumbnailImageUrl, packPricingTypeId);
      }}
      text="Edit Licensing"
    />
  );

  const convertTrialLicensesButton = (
    <EditLinkButton
      key={cn("ConvertTrialLicensesButton-", id)}
      onClick={() => {
        onConvertTrialLicense?.(id, packPricingTypeId);
      }}
      text="Edit Licensing"
    />
  );

  const removeButton = (
    <RemoveLinkButton
      key={cn("RemovePackButton-", id)}
      onClick={() => {
        onRemovePackFromAccount?.(id);
      }}
    />
  );

  const archiveButton = (
    <ArchiveLinkButton
      key={cn("ArchivePackButton-", id)}
      onClick={() => {
        onArchivePack?.(id);
      }}
    />
  );

  const restoreButton = (
    <RestoreLinkButton
      key={cn("RestorePackButton-", id)}
      onClick={() => {
        onRestorePack?.(id, title, thumbnailImageUrl, packPricingTypeId);
      }}
    />
  );

  const purchaseNowButton = (
    <EditLinkButton key={cn("PurchaseNowButton-", id)} onClick={togglePaymentModal} text="Purchase now" />
  );

  const getButtons = () => {
    const actionButtons = [];

    const shouldShowEditLicensingButton = () => {
      if (isTrial || isExpired || (!isFree && !hasGlobalAccess) || isOwnPack) return false;

      const hasLicenseLimits = licenseCount > 0;
      const hasExpirationDate = !!expirationDate;

      if (!hasLicenseLimits && !hasExpirationDate) return false;

      if (hasGlobalAccess && !hasPurchasePackAddon) {
        return hasLicenseLimits || hasExpirationDate;
      }

      return hasPurchasePackAddon && (hasLicenseLimits || hasExpirationDate) && !isOwnPack;
    };

    const shouldDisableEditLicensingButton =
      !hasGlobalAccess && hasPurchasePackAddon && isFree && (licenseCount > 0 || !!expirationDate) && !hasPurchasePower;

    if (shouldShowEditLicensingButton()) {
      actionButtons.push(getEditLicensingButton(shouldDisableEditLicensingButton));
    }

    if (shouldShowPurchaseButton) {
      actionButtons.push(purchaseNowButton);
    }

    if (convertTrialLicenseButton) {
      actionButtons.push(convertTrialLicensesButton);
    }

    if (
      isExpired &&
      !isArchived &&
      onExtendLicenses &&
      !isOwnPack &&
      hasGlobalAccess &&
      (!isAccountHome || hasPurchasePower)
    ) {
      actionButtons.push(extendLicensesButton);
    }

    if (
      isFunction(onRemovePackFromAccount) &&
      isFunction(isAllowedToRemovePackFromAccount) &&
      isAllowedToRemovePackFromAccount?.()
    ) {
      actionButtons.push(removeButton);
    }

    if (onArchivePack && !isOwnPack) {
      actionButtons.push(archiveButton);
    }

    const shouldShowRestoreButton = () => {
      if (!onRestorePack || isOwnPack) return false;

      if (hasGlobalAccess) return true;

      if (isAccountHome) return hasPurchasePower && isFreeOrOther;

      return isFreeOrOther;
    };

    if (shouldShowRestoreButton()) {
      actionButtons.push(restoreButton);
    }

    return actionButtons;
  };

  const buttons = getButtons();

  const renderMenuContent = () => {
    const hasButtons = buttons.length > 0;
    const shouldShowMenu = hasAccountManageRole && !hasSource;

    if (shouldShowMenu && hasButtons) {
      return <EllipsisPopupButton circle>{[buttons]}</EllipsisPopupButton>;
    }

    if (!shouldShowMenu) {
      return (
        <div data-testid="disabledCardEllipsis" onClick={(e) => e.stopPropagation()}>
          <EllipsisPopupButton disabled circle>
            {[]}
          </EllipsisPopupButton>
        </div>
      );
    }

    return null;
  };

  const menuContent = renderMenuContent();

  const paymentPacks: PaymentPack[] = useMemo(
    () => [{ packName: title, packId: id, isExpired, isTrial, billingTerm, rawPricePerUser, isPurchased }],
    [billingTerm, id, isExpired, isPurchased, isTrial, rawPricePerUser, title],
  );

  if (!menuContent) {
    return null;
  }

  return (
    <>
      {menuContent}
      {isPaymentModalOpen && (
        <SinglePaymentModalContainer
          paymentPacks={paymentPacks}
          onPaymentSuccess={handlePaymentSuccess}
          onClose={togglePaymentModal}
        />
      )}
    </>
  );
};
